import React, { useState, useEffect } from "react";
import { Button, Form, FormGroup, Input, Label, Alert, Spinner } from "reactstrap";
import axios from "axios";
import { API_URL_partidas_curso_abierto, API_URL_partners } from "../../../constants";

import { getAuthHeaders } from "../../../utils/auth";
const CursoAbiertoFor = ({
  proyectoCode,
  proyectoName,
  proyectoMoneda,
  estudiante = null,
  resetState,
  toggle
}) => {
  const [formData, setFormData] = useState({
    pk: '',
    nombre: '',
    estudiante: '',
    telefono: '',
    correo: '',
    precio: '',
    referencia: '',
    observaciones: '',
    moneda: '',
    tipo_cambio: null,
    proyecto_code: '',
    miembro_vendedor: '',
    partner_vendedor: '',
    comision_vendedor: ''
  });

  const [partners, setPartners] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [validationErrors, setValidationErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    // Fetch partners
    const fetchPartners = async () => {
      const headers = getAuthHeaders();
      
      try {
        const response = await axios.get(API_URL_partners, { headers });
        // Sort partners alphabetically by partner name
        const sortedPartners = response.data.sort((a, b) => {
          if (a.partner < b.partner) return -1;
          if (a.partner > b.partner) return 1;
          return 0;
        });
        setPartners(sortedPartners);
      } catch (error) {
        console.error('Error fetching partners:', error);
      }
    };

    fetchPartners();

    // Set project code
    setFormData(prev => ({
      ...prev,
      proyecto_code: proyectoCode
    }));

    // If editing, load estudiante data
    if (estudiante) {
      const {
        pk,
        estudiante: estudianteValue,
        nombre,
        telefono,
        correo,
        precio,
        referencia,
        observaciones,
        moneda,
        tipo_cambio,
        proyecto_code,
        miembro_vendedor,
        partner_vendedor,
        comision_vendedor
      } = estudiante;

      setFormData({
        pk: pk || '',
        estudiante: estudianteValue || '',
        nombre: nombre || '',
        telefono: telefono || '',
        correo: correo || '',
        precio: precio || '',
        referencia: referencia || '',
        observaciones: observaciones || '',
        moneda: moneda || '',
        tipo_cambio: tipo_cambio ?? null,
        proyecto_code: proyecto_code || proyectoCode,
        miembro_vendedor: miembro_vendedor || '',
        partner_vendedor: partner_vendedor || '',
        comision_vendedor: comision_vendedor || ''
      });
    }
  }, [proyectoCode, estudiante]);

  const validateForm = () => {
    const errors = {};
    const { nombre, precio, moneda, comision_vendedor } = formData;

    // Required fields validation
    if (!nombre || nombre.trim() === '') {
      errors.nombre = 'Por favor ingresa el nombre del estudiante';
    }

    if (!precio || precio === '') {
      errors.precio = 'Por favor ingresa el precio por estudiante';
    } else if (parseFloat(precio) < 0) {
      errors.precio = 'El precio no puede ser negativo';
    }

    if (!moneda || moneda === '') {
      errors.moneda = 'Por favor selecciona una moneda';
    }

    // ✅ ADDED: Comision vendedor validation
    if (!comision_vendedor || comision_vendedor === '') {
      errors.comision_vendedor = 'Por favor ingresa el porcentaje de comisión';
    } else if (parseFloat(comision_vendedor) < 0) {
      errors.comision_vendedor = 'El porcentaje no puede ser negativo';
    } else if (parseFloat(comision_vendedor) > 100) {
      errors.comision_vendedor = 'El porcentaje no puede ser mayor a 100';
    }

    // Email validation (optional field, but validate format if provided)
    if (formData.correo && formData.correo.trim() !== '') {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.correo)) {
        errors.correo = 'Por favor ingresa un correo electrónico válido';
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const onChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear validation error for this field
    if (validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form before submitting
    if (!validateForm()) {
      setErrorMessage('Por favor completa todos los campos obligatorios marcados con *');
      // Focus first invalid field
      setTimeout(() => {
        const firstError = document.querySelector('.is-invalid-custom');
        if (firstError) {
          firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
          const input = firstError.querySelector('input, select');
          if (input) input.focus();
        }
      }, 100);
      return;
    }

    setIsSubmitting(true);
    setErrorMessage('');

    const headers = getAuthHeaders();

    try {
      if (estudiante) {
        // ✅ FIXED: Added trailing slash
        const url = `${API_URL_partidas_curso_abierto}${formData.pk}/`;
        await axios.put(url, formData, { headers });
      } else {
        await axios.post(API_URL_partidas_curso_abierto, formData, { headers });
      }

      resetState();
      toggle();
    } catch (error) {
      console.error("Error saving estudiante:", error);

      // Handle server-side validation errors
      if (error.response?.data) {
        const serverErrors = error.response.data;
        if (typeof serverErrors === 'object' && !Array.isArray(serverErrors)) {
          setValidationErrors(serverErrors);
          setErrorMessage('Error de validación. Por favor revisa los campos.');
        } else {
          setErrorMessage(estudiante ? 'Error al editar el estudiante.' : 'Error al agregar el estudiante.');
        }
      } else {
        setErrorMessage('Error de conexión. Por favor intenta nuevamente.');
      }
      setIsSubmitting(false);
    }
  };

  const defaultIfEmpty = (value) => {
    return value === "" ? "" : value;
  };

  const h3Style = { color: 'blue' };

  const fxRowCurrency = formData.moneda || "USD";
  const fxProjectCurrency = proyectoMoneda || "USD";
  const fxCurrency = fxRowCurrency !== "USD" ? fxRowCurrency : fxProjectCurrency;
  const showTipoCambio = fxRowCurrency !== "USD" || fxProjectCurrency !== "USD";

  return (
    <div>
      <h3>{proyectoName}</h3>
      {proyectoCode && <h3 style={h3Style}>{proyectoCode.toUpperCase()}</h3>}
      
      <div className="form-requirements-note">
        <strong>Nota:</strong> Los campos marcados con <span className="required-asterisk">*</span> son obligatorios.
      </div>

      <Form onSubmit={handleSubmit}>
        <FormGroup>
          <Label for="nombre">
            Nombre de estudiante<span className="required-asterisk">*</span>
          </Label>
          <Input
            type="text"
            name="nombre"
            onChange={onChange}
            value={defaultIfEmpty(formData.nombre)}
            className={validationErrors.nombre ? 'is-invalid-custom' : ''}
            placeholder="Ingresa nombre del estudiante"
          />
          {validationErrors.nombre && (
            <div className="invalid-feedback-custom">{validationErrors.nombre}</div>
          )}
        </FormGroup>

        <FormGroup>
          <Label for="partner_vendedor">Selecciona Partner de Vendedor:</Label>
          <Input
            type="select"
            name="partner_vendedor"
            value={formData.partner_vendedor}
            onChange={onChange}
          >
            <option value="">Partner:</option>
            {Array.isArray(partners) && partners.map(partner => (
              <option key={partner.pk} value={partner.partner}>
                {partner.partner}
              </option>
            ))}
          </Input>
        </FormGroup>

        <FormGroup>
          <Label for="miembro_vendedor">Selecciona Miembro Vendedor:</Label>
          <Input
            type="select"
            name="miembro_vendedor"
            value={formData.miembro_vendedor}
            onChange={onChange}
          >
            <option value="">Miembro:</option>
            {Array.isArray(partners) && partners.map(partner => (
              <option key={partner.pk} value={partner.partner}>
                {partner.partner}
              </option>
            ))}
          </Input>
        </FormGroup>

        <FormGroup>
          <Label for="comision_vendedor">
            Selecciona % de comision del Vendedor<span className="required-asterisk">*</span>
          </Label>
          <Input
            type="number"
            name="comision_vendedor"
            value={formData.comision_vendedor}
            onChange={onChange}
            step="0.01"
            placeholder="0.00"
            className={validationErrors.comision_vendedor ? 'is-invalid-custom' : ''}
          />
          {validationErrors.comision_vendedor && (
            <div className="invalid-feedback-custom">{validationErrors.comision_vendedor}</div>
          )}
        </FormGroup>

        <FormGroup>
          <Label for="correo">Correo:</Label>
          <Input
            type="email"
            name="correo"
            onChange={onChange}
            value={defaultIfEmpty(formData.correo)}
            className={validationErrors.correo ? 'is-invalid-custom' : ''}
            placeholder="ejemplo@correo.com"
          />
          {validationErrors.correo && (
            <div className="invalid-feedback-custom">{validationErrors.correo}</div>
          )}
        </FormGroup>

        <FormGroup>
          <Label for="telefono">Telefono:</Label>
          <Input
            type="text"
            name="telefono"
            onChange={onChange}
            value={defaultIfEmpty(formData.telefono)}
            placeholder="Ingresa teléfono"
          />
        </FormGroup>

        <FormGroup>
          <Label for="precio">
            Precio x estudiante<span className="required-asterisk">*</span>
          </Label>
          <Input
            type="number"
            name="precio"
            onChange={onChange}
            value={defaultIfEmpty(formData.precio)}
            step="0.01"
            className={validationErrors.precio ? 'is-invalid-custom' : ''}
            placeholder="0.00"
          />
          {validationErrors.precio && (
            <div className="invalid-feedback-custom">{validationErrors.precio}</div>
          )}
        </FormGroup>

        <FormGroup>
          <Label for="moneda">
            Moneda<span className="required-asterisk">*</span>
          </Label>
          <Input
            type="select"
            name="moneda"
            value={defaultIfEmpty(formData.moneda)}
            onChange={onChange}
            className={validationErrors.moneda ? 'is-invalid-custom' : ''}
          >
            <option value="">Selecciona moneda</option>
            <option value="MXN">MXN</option>
            <option value="USD">USD</option>
            <option value="EUR">EUR</option>
            <option value="COP">COP</option>
          </Input>
          {validationErrors.moneda && (
            <div className="invalid-feedback-custom">{validationErrors.moneda}</div>
          )}
        </FormGroup>

        {showTipoCambio && (
          <FormGroup>
            <Label for="tipo_cambio">
              Tipo de Cambio ({fxCurrency} por 1 USD):
            </Label>
            <Input
              type="number"
              step="0.0001"
              min="0"
              name="tipo_cambio"
              placeholder="ej. 17 (vacío para usar la tasa mensual)"
              onChange={(e) => setFormData(prev => ({
                ...prev,
                tipo_cambio: e.target.value === "" ? null : Number(e.target.value),
              }))}
              value={formData.tipo_cambio ?? ""}
            />
            <small className="form-text text-muted">
              {fxRowCurrency !== "USD"
                ? "Opcional. Si lo capturas, esta partida se convierte a USD dividiendo entre este número. Vacío = tasa del mes de inicio del proyecto."
                : `Opcional. Si lo capturas, esta partida en USD se convierte a ${fxProjectCurrency} multiplicando por este número. Vacío = tasa del mes de inicio del proyecto.`}
            </small>
          </FormGroup>
        )}

        <FormGroup>
          <Label for="referencia">Referencia:</Label>
          <Input
            type="text"
            name="referencia"
            onChange={onChange}
            value={defaultIfEmpty(formData.referencia)}
            placeholder="Ingresa referencia"
          />
        </FormGroup>

        <FormGroup>
          <Label for="observaciones">Observaciones:</Label>
          <Input
            type="textarea"
            name="observaciones"
            onChange={onChange}
            value={defaultIfEmpty(formData.observaciones)}
            placeholder="Notas sobre precio especial, descuento, beca, etc. (opcional)"
            rows={2}
          />
        </FormGroup>

        {errorMessage && <Alert color="danger">{errorMessage}</Alert>}
        
        <Button color="primary" disabled={isSubmitting}>
          {isSubmitting ? (
            <>
              <Spinner size="sm" className="me-2" /> Guardando...
            </>
          ) : (
            'Guardar'
          )}
        </Button>
      </Form>
    </div>
  );
};

export default CursoAbiertoFor;